import { useEffect, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "../../../examples/Tables/DataTable";
import data from "../../dashboard/components/Projects/data";
import useApi from "../../../components/Utils/api";
import toast from "react-hot-toast";
import { DatePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import Papa from 'papaparse';
import TransactionsDialog from "./TransactionsDialog";

function CardSettlementReport() {
  const { cardSettlementReportsColumns } = data();
  const [summary, setSummary] = useState([]);
  const [openTransactionsDialog, setOpenTransactionsDialog] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);

  // Function to get today's date at midnight in AEST timezone
  const getTodayDateAtMidnight = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Set to start of the day in local time
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000); // Convert to local midnight in AEST
  };

  // Initialize selectedDate to today's date at local midnight
  const [selectedDate, setSelectedDate] = useState(
    getTodayDateAtMidnight().toISOString().slice(0, 10)
  );

  const api = useApi();

  // Format date to 'yyyy-MM-dd' and adjust to AEST timezone
  const formatDate = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      console.error("Invalid date provided:", date);
      return '';
    }
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 10);
  };

  const handleViewTransactions = (settlement) => {
    setSelectedSettlement(settlement);
    setOpenTransactionsDialog(true);
  };

  const handleCloseTransactionsDialog = () => {
    setOpenTransactionsDialog(false);
    setSelectedSettlement(null);
  };

  const getCardSettlementReport = (date) => {
    if (!date) return;

    const selectedDateObj = new Date(date);

    const payload = {
      date: formatDate(selectedDateObj),
    };

    api
      .post("/adminPortal/reports/settlement/pos", payload)
      .then((response) => {
        const { responseCode, data: rows, message } = response.data || {};

        // Success but no records
        if (responseCode === 200 && (!rows || rows.length === 0)) {
          setSummary([]);
          return;
        }

        // Success with data
        if (responseCode === 200) {
          // Sort by batchId to group them together
          const sortedData = [...rows].sort((a, b) =>
            (a.batchId || '').localeCompare(b.batchId || '')
          );

          const modifiedData = sortedData.map((item) => ({
            ...item,
            action: (
              <MDButton
                variant="contained"
                size="small"
                color="info"
                onClick={() => handleViewTransactions(item)}
              >
                View Transactions
              </MDButton>
            ),
          }));

          setSummary(modifiedData);
          return;
        }

        // Business-level failure
        toast.error(message || "Failed to fetch card settlement report", {
          duration: 1000,
        });
      })
      .catch((error) => {
        // Authorization error
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }

        toast.error("Something went wrong. Please try again later.", {
          duration: 1000,
        });
      });
  };

  const generateCSV = () => {
    if (summary.length === 0) {
      return '';
    }

    const headers = cardSettlementReportsColumns.map(column => column.Header);

    const summaryWithColumns = summary.map(item => {
      return cardSettlementReportsColumns.map(column => {
        const accessor = column.accessor;
        const value = typeof accessor === 'function' ? accessor(item) : item[accessor];
        return value;
      });
    });

    const csvData = [headers, ...summaryWithColumns];

    const csv = Papa.unparse(csvData, {
      quotes: true,
    });

    return csv;
  };

  const handleDownloadCSV = () => {
    if (summary.length === 0) {
      const toastId = toast.error("No records found to download", { autoClose: false });
      setTimeout(() => toast.dismiss(toastId), 1000);
      return;
    }

    const csvData = generateCSV();

    const currentDate = new Date().toISOString().slice(0, 10);
    const currentTime = new Date().toLocaleTimeString().replace(/:/g, '-');
    const dateTimeString = `${currentDate}_${currentTime}`;

    const fileName = `CardSettlementReport_${dateTimeString}.csv`;

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  useEffect(() => {
    if (selectedDate !== null) {
      getCardSettlementReport(selectedDate);
    }
  }, [selectedDate]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12}>
            <MDBox mb={3} position="relative" display="flex" justifyContent="center">
              <DatePicker
                disabledDate={afterToday()}
                onChange={(date) => {
                  if (date) {
                    setSelectedDate(date);
                  }
                }}
                format="dd MMM yyyy"
                value={selectedDate ? new Date(selectedDate) : null}
                placeholder="Select Date"
              />
              <MDButton
                variant="gradient"
                onClick={handleDownloadCSV}
                color="info"
                style={{ position: "absolute", right: 0, top: 0 }}
              >
                Export
              </MDButton>
            </MDBox>

            <DataTable
              table={{ columns: cardSettlementReportsColumns, rows: summary }}
              showTotalEntries={true}
              isSorted={false}
              noEndBorder
              entriesPerPage={{ defaultValue: 25 }}
              entriesPerPagePosition="bottom-right"
              canSearch={true}
            />
          </Grid>
        </Grid>
      </MDBox>

      <TransactionsDialog
        open={openTransactionsDialog}
        onClose={handleCloseTransactionsDialog}
        transactions={selectedSettlement?.transactions || []}
        settlementRecord={selectedSettlement}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default CardSettlementReport;
